import React, { ChangeEvent, useState } from 'react'
import { BankrateLoanTerms } from '@bankrate/mortgage-types'

import { useStoreActions, useStoreState } from 'store'
import { FHAButtonGroup, MilitaryStatusButtonGroup } from './../inputs'

type Term = { label: string; value: BankrateLoanTerms[] }
const termList: Term[] = [
  {
    label: '30 yr fixed',
    value: ['30yr'],
  },
  {
    label: '20 yr fixed',
    value: ['20yr'],
  },
  {
    label: '15 yr fixed',
    value: ['15yr'],
  },
  {
    label: '10 yr fixed',
    value: ['10yr'],
  },
  {
    label: '3 yr ARM',
    value: ['3-1arm', '3-6arm'],
  },
  {
    label: '5 yr ARM',
    value: ['5-1arm', '5-6arm'],
  },
  {
    label: '7 yr ARM',
    value: ['7-1arm', '7-6arm'],
  },
  {
    label: '10 yr ARM',
    value: ['10-1arm', '10-6arm'],
  },
]

export default function LoanOptions() {
  const { onInputTracking } = useStoreActions(actions => actions.tracking)
  const mortgageType = useStoreState(state => state.dimensions.dimensions.mortgageType)
  const updateLoanTerms = useStoreActions(actions => actions.dimensions.updateLoanTerms)
  const purchaseLoanTerms = useStoreState(state => state.dimensions.dimensions.purchase.loanTerms)
  const refinanceLoanTerms = useStoreState(state => state.dimensions.dimensions.refinance.loanTerms)
  const [toggle, setToggle] = useState(false)

  const handleChange = (term: Term) => (event: ChangeEvent<HTMLInputElement>) => {
    const selectedTerms = mortgageType === 'Purchase' ? purchaseLoanTerms : refinanceLoanTerms

    // Check what the new state will be and prevent changing
    // it if we will not have any terms, since multiple terms
    // can be added from a single ARM selection
    let newTerms = [...selectedTerms]
    term.value.forEach(singleTerm => {
      const index = newTerms.indexOf(singleTerm)

      if (index >= 0) {
        newTerms.splice(index, 1)
      }
    })

    if (newTerms.length === 0) {
      event.preventDefault()
      event.target.checked = true
      return
    }

    updateLoanTerms(term.value)
    // Since term value is an array with multiple loanOptions in arm products,
    // the data team suggested we also use the label in the name field.
    onInputTracking({
      name: term.value.length > 1 ? term.label : term.value[0],
      type: 'TERM',
      label: term.label,
      value: event.target.checked,
    })
  }

  return (
    <div className="">
      <div className="display-flex flex-wrap pt-2" id="mortgageTerms" data-testid="loanOptions">
        {termList.map((term, index) => {
          if (index <= 3) {
            return (
              <div key={`${term.value}-${index}`} className={`w-1/2 md:w-1/3 lg:w-1/2 ${index < 2 ? '' : 'mt-4'} px-1`}>
                <label className="FormCheckbox" htmlFor={`checkbox-${term.value}`}>
                  <input
                    value={term.value}
                    className="FormCheckbox-input"
                    type="checkbox"
                    id={`checkbox-${term.value}`}
                    defaultChecked={
                      mortgageType === 'Purchase'
                        ? purchaseLoanTerms.includes(term.value[0])
                        : refinanceLoanTerms.includes(term.value[0])
                    }
                    onChange={handleChange(term)}
                  />
                  <span className="FormCheckbox-fauxInput Icon Icon--sm">
                    <svg className="Icon-glyph" viewBox="0 0 12 9" fillRule="evenodd">
                      <path d="M4.1 6.1L1.4 3.4 0 4.9 4.1 9l7.6-7.6L10.3 0z"></path>
                    </svg>
                  </span>
                  {term.label}
                </label>
              </div>
            )
          } else {
            return (
              <div
                key={`${term.value}-${index}`}
                className={`w-1/2 md:w-1/3 lg:w-1/2 mt-4 px-1 ${toggle ? '' : 'display-none'}`}
              >
                <label className="FormCheckbox" htmlFor={`checkbox-${term.value}`}>
                  <input
                    value={term.value}
                    className="FormCheckbox-input"
                    type="checkbox"
                    id={`checkbox-${term.value}`}
                    defaultChecked={
                      mortgageType === 'Purchase'
                        ? purchaseLoanTerms.includes(term.value[0])
                        : refinanceLoanTerms.includes(term.value[0])
                    }
                    onChange={handleChange(term)}
                  />
                  <span className="FormCheckbox-fauxInput Icon Icon--sm">
                    <svg className="Icon-glyph" viewBox="0 0 12 9" fillRule="evenodd">
                      <path d="M4.1 6.1L1.4 3.4 0 4.9 4.1 9l7.6-7.6L10.3 0z"></path>
                    </svg>
                  </span>
                  {term.label}
                </label>
              </div>
            )
          }
        })}
      </div>
      <button
        data-testid="show"
        className="Button Button--reset type-body-two p-0 text-blue"
        onClick={() => setToggle(!toggle)}
      >
        Show {toggle ? 'less' : 'more'}
      </button>
      <FHAButtonGroup />
      <MilitaryStatusButtonGroup />
    </div>
  )
}
